'use client'

import { Button } from '@/components/Button/Button'
import useCartStore from '@/store/useCartStore'
import { FilteredTicketType } from '@/types/AdminPanelType'
import { CartTicket } from '@/types/CartType'
import { groupCartTicketsBySection } from '@/utils/CartUtils'

import { format, parseISO } from 'date-fns'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import styles from './FloatingCart.module.scss'

type FloatingCartProps = {
  ticketSections: FilteredTicketType[]
}

function getQtyString(tickets: CartTicket[]): string {

  const strings: string[] = []

  let adultPriceSum = 0
  let childPricedSum = 0
  let generalPricedSum = 0

  const numAdult = tickets.find(t => t.data.type_ticket === 'Adult')?.qty ?? 0
  const numChild = tickets.find(t => t.data.type_ticket === 'Child')?.qty ?? 0
  const numGeneral = tickets.find(t => t.data.type_ticket === 'General')?.qty ?? 0

  const adultPrice = numAdult ? tickets.find(t => t.data.type_ticket === 'Adult')?.price : 0
  const childPrice = numChild ? tickets.find(t => t.data.type_ticket === 'Child')?.price : 0
  const generalPrice = numGeneral ? tickets.find(t => t.data.type_ticket === 'General')?.price : 0

  if(adultPrice) adultPriceSum = Number(parseFloat(adultPrice).toFixed(2)) * numAdult
  if(childPrice) childPricedSum = Number(parseFloat(childPrice).toFixed(2))  * numChild
  if(generalPrice) generalPricedSum = Number(parseFloat(generalPrice).toFixed(2)) * numGeneral

  if (numAdult) strings.push(`${numAdult} Adult (+10yrs) - £${adultPriceSum.toFixed(2)}`)
  if (numChild) strings.push(`${numChild} Child (3-9yrs) - £${childPricedSum.toFixed(2)}`)
  if (numGeneral) strings.push(`${numGeneral} All Ages - £${generalPricedSum.toFixed(2)}`)

  return strings.join(', ')
}

function sectionHeader(sectionTitle: string): JSX.Element | null {

  // TODO: If needed the section title can be wrapped in a link so it can be clicked to scroll to the section?

  //  TODO: Loading state server side react suspense skeleton loading component ??? https://beta.nextjs.org/docs/routing/loading-ui, https://beta.reactjs.org/apis/react/Suspense

  if (sectionTitle) {
    return (
      <div className={styles.sectionHeader}>
        <h3>
          {sectionTitle}
        </h3>
      </div>
    )
  }
  return null
}


export function FloatingCart({ticketSections}: FloatingCartProps) {

  const cart = useCartStore(state => state.cart)
  const cartMetaTotalCount = useCartStore(state => state.cartMetaTotalCount)
  const getCart = useCartStore(state => state.getCart)
  const removeAllFromCart = useCartStore(state => state.removeAllFromCart)
  const cartLoading = useCartStore(state => state.cartLoading)

  const [loading, setLoading] = useState<boolean>(cartLoading)
  const [isCartHidden, setIsCartHidden] = useState(true)

  function removeLink(ticketIds: Array<string>, dateOrYearStr?: string) {
    return <Button onClick={() => removeAllFromCart(ticketIds, dateOrYearStr)}> Remove</Button>
  }

      const cartButton = <Link passHref href='/cart' className={styles.basketLink}><span className={styles.count}>{ cartMetaTotalCount }</span> <span className={styles.visuallyHidden}>items in the cart</span></Link>

      const containerStyles = [ styles.floatingCart ]
      if (loading) containerStyles.push(styles.loading)

      const newItems = groupCartTicketsBySection(cart.data.tickets, ticketSections)

      const titleSection: Array<JSX.Element> = []

      Object.keys(newItems).forEach(section => {

        const sectionProducts: Array<JSX.Element> = []

        if (Object.values(newItems[section]).length) {
          const tickets = Object.values(newItems[section])
          newItems[section].map(ticket => {
            sectionProducts.push(
            <div key={ ticket.ticket_id } className={ styles.rowItem }>
              <div>
                { ticket.title }<br />
                <div className={ styles.ticketDate }>
                  { ticket.date ? format(parseISO(ticket.date), 'MMMM d, yyyy') : ticket.year }
                </div>
                <div className={ styles.qty }>
                  { getQtyString(tickets) }
                </div>
              </div>
              { removeLink([ticket.ticket_id]) }
            </div>
          )
          })
        }

        if(sectionProducts.length > 0) {
          titleSection.push(
          <div key={ section } className={ styles.rowContainer }>
            { sectionHeader(section) }
            { sectionProducts }
          </div>
        )
        }
      })

      const subTotal = cart.meta.total_price ? <b>&pound;{ parseInt(cart.meta.total_price) }</b> : ''

      useEffect(() => {
        getCart()
      // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [])

        return (
          <section className={containerStyles.join(' ')}>
            <div className={styles.cart + ' ' +( !isCartHidden && styles.cartShow)}>
              <Button className={styles.toggleBtn} onClick={() => setIsCartHidden(!isCartHidden)}><span className={styles.visuallyHidden}>Toggle cart</span></Button>
              <div className={styles.cartHeader}>
                <div>Subtotal: { subTotal }</div>
                { cartButton }
              </div>
              <div className={styles.cartItems}>
                {cart.data.tickets.length < 1 ? <p className={styles.emptyMessage}>Your cart is empty</p> : ''}
                { titleSection }
                <Link href='/cart/checkout'>
                  <Button disabled={cart.data.tickets.length < 1} loading={loading} onClick={() =>setLoading(true)} className={styles.btnMobile}>
                    Checkout
                  </Button>
                </Link>
              </div>
            </div>
            <Link href='/cart/checkout'>
              <Button disabled={cart.data.tickets.length < 1} loading={loading} onClick={() =>setLoading(true)} className={styles.btn}>
                Checkout
              </Button>
            </Link>
          </section>
        )
}
